import {
  type CreateTableOverlayConfig,
  createTableOverlay,
} from "@/components/discovery/table-overlays/createTableOverlay";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";

/**
 * Row data shape for the podcast episodes table (AG Grid column fields).
 * Maps from {@link PodcastEpisode} when building the datasource.
 */
export type PodcastEpisodeRowData = {
  ID: string;
  TITLE: string | null;
  SUBTITLE: string | null;
  DESCRIPTION: string | null;
  AUTHOR: string | null;
  SOURCE_TITLE: string | null;
  SOURCE_COUNTRY: string | null;
  LANGUAGE: string | null;
  PUBLISH_DATE: string | null;
  DURATION: string | null;
  ARCHIVE_URL: string | null;
  IMAGE_URL: string | null;
  STATUS: string | null;
  ESTIMATED_AUDIENCE: number | null;
  TRANSCRIPT_EXCERPT: string | null;
};

const usePodcastEpisodesTableState: CreateTableOverlayConfig["useTableState"] =
  () => {
    const error = useDiscoveryContentManagementStore(
      (state) => state.podcastEpisodesError,
    );
    const isFetching = useDiscoveryContentManagementStore(
      (state) => state.isFetchingPodcastEpisodes,
    );
    const totalCount = useDiscoveryContentManagementStore(
      (state) => state.totalPodcastEpisodesCount,
    );
    const isInitialLoading = useDiscoveryContentManagementStore(
      (state) => state.isPodcastEpisodesInitialLoading,
    );
    return { error, isFetching, totalCount, isInitialLoading };
  };

const usePodcastEpisodesNoRowsActions: CreateTableOverlayConfig["useNoRowsActions"] =
  () => {
    const searchTerm = useDiscoveryContentManagementStore(
      (state) => state.podcastEpisodesSearchTerm,
    );
    const setPodcastEpisodesSearchTerm = useDiscoveryContentManagementStore(
      (state) => state.setPodcastEpisodesSearchTerm,
    );
    const resetPodcastEpisodesTableState = useDiscoveryContentManagementStore(
      (state) => state.resetPodcastEpisodesTableState,
    );

    const isAppliedFilters = !!searchTerm;
    const onReset = () => {
      setPodcastEpisodesSearchTerm("");
      resetPodcastEpisodesTableState();
    };
    return { isAppliedFilters, onReset };
  };

const usePodcastEpisodesClearError: CreateTableOverlayConfig["useClearError"] =
  () => {
    const setPodcastEpisodesError = useDiscoveryContentManagementStore(
      (state) => state.setPodcastEpisodesError,
    );
    return () => setPodcastEpisodesError(null);
  };

const podcastEpisodesTableOverlayConfig: CreateTableOverlayConfig = {
  useTableState: usePodcastEpisodesTableState,
  noRowsMessageWithFilters: "No Podcast Episodes Found for Applied Filters",
  noRowsMessageWithoutFilters: "No Podcast Episodes Found",
  useNoRowsActions: usePodcastEpisodesNoRowsActions,
  errorTitle: "Failed to Load Podcast Episodes",
  useClearError: usePodcastEpisodesClearError,
};

/** Creates the unified overlay component for the podcast episodes table. */
export const createPodcastEpisodesUnifiedOverlay = (onRetry?: () => void) =>
  createTableOverlay(podcastEpisodesTableOverlayConfig, onRetry);
